import { ChatAttachment } from '@src/api/shared/community/chatApiClient';
import { COMMUNITY_IMAGE_REGEX } from '@src/constants/fileTypes';
import PreviewFileList from '@src/features/shared/community/components/chat/PreviewFileList';
import PreviewImageModal from '@src/features/shared/community/components/chat/PreviewImageModal';
import { Attachment } from '@src/idb';
import React, { useState } from 'react';

export interface AttachmentPreviewProps {
  attachments: Attachment[];
  onDelete: (attachment: Attachment) => Promise<void>;
  allowFileAttachment?: boolean;
  className?: string;
}

/**
 * AttachmentPreview Component
 * Handles display and interaction with file attachments in the editor
 */
export const AttachmentPreview: React.FC<AttachmentPreviewProps> = ({
  attachments,
  onDelete,
  allowFileAttachment,
  className = '',
}) => {
  const [isPreviewImageModalOpen, setIsPreviewImageModalOpen] = useState(false);
  const [selectedAttachmentForImageModal, setSelectedAttachmentForImageModal] =
    useState<ChatAttachment | null>(null);
  const [currentBlobUrl, setCurrentBlobUrl] = useState<string | null>(null);

  const handleImagePreviewItemClick = (attachment: Attachment) => {
    if (COMMUNITY_IMAGE_REGEX.test(attachment.name)) {
      const objectURL =
        attachment.blob instanceof File ? URL.createObjectURL(attachment.blob) : null;
      setCurrentBlobUrl(objectURL);

      const modalAttachmentData: ChatAttachment = {
        id: 0,
        file_url: objectURL ?? attachment.chatAttachment?.file_url ?? '',
        file_name: attachment.name,
        extension: attachment.type.split('/')[1] || '',
        blob: attachment.blob,
      };
      setSelectedAttachmentForImageModal(modalAttachmentData);
      setIsPreviewImageModalOpen(true);
    }
  };

  const handleClosePreviewModal = () => {
    setIsPreviewImageModalOpen(false);
    setSelectedAttachmentForImageModal(null);
    if (currentBlobUrl) {
      URL.revokeObjectURL(currentBlobUrl);
      setCurrentBlobUrl(null);
    }
  };

  if (attachments.length === 0 || !allowFileAttachment) {
    return null;
  }

  return (
    <>
      <div className={`relative z-50 mt-4 ${className}`}>
        <PreviewFileList
          attachments={attachments}
          upload={{
            enabled: true,
            onDelete: onDelete,
          }}
          onImageItemClick={handleImagePreviewItemClick}
        />
      </div>

      {isPreviewImageModalOpen && selectedAttachmentForImageModal && (
        <PreviewImageModal
          attachment={selectedAttachmentForImageModal}
          onCancel={handleClosePreviewModal}
          isDraft={true}
        />
      )}
    </>
  );
};

export default AttachmentPreview;
